import React, {useEffect, useMemo} from 'react';
import {Box, Button, Typography} from '@mui/material';
import {recommendFieldsType} from '../CreateRecommend/recommendFields';
import type {setUpNewFields} from './setUpNewFields';
import SourceDestination from '../CreateRecommend/RecommendSourceDestination/SourceDestination';
import {
  RecommendProps,
  getValidation,
} from '../CreateRecommend/RecommendSourceDestination/validators';
import {NewPlan} from './NewPlan';
import {OVRecommendActionEnum} from '../../../schema-types/globalTypes';
import {NEW_PLAN_ID} from '../CreateRecommend/RecommendSourceDestination/constants';
import {useResetReccs} from '../CreateRecommend/RecommendSourceDestination/useResetReccs';
import {ProtectionPlan} from './ProtectionPlan';
import {useRecommendContext} from '../CreateRecommend/CreateRecommendHelpers';

import {
  useAutoForm,
  useAutoField,
  useLoadedAutoField,
} from '../../../components/AutoForm/useAutoForm';
import {POTS_ANNUITY, POTS_BOND, POTS_PROTECTION} from '../../../utils/potsFilter';
import {AnnuityPlan} from './AnnuityPlan';
import {loadSavedSetUpNewPlan, useDeriveIsLowesPension} from '../hooks/setUpNewHooks';
import {SetupNewConvert} from './SetupNewConvert';

const SetUpNew = ({
  contactName,
  sourceDestinationLabels,
  destinationPots,
  sourcePots,
}: RecommendProps) => {
  useResetReccs(OVRecommendActionEnum.SET_UP_NEW);
  const [setUpNewFieldObj, setUpNewNewPlanMeta, setUpNewFieldHelpers] = useAutoField<
    setUpNewFields['newPlan'] | undefined
  >('setUpNew.newPlan');

  const {setLoaded} = useLoadedAutoField();
  const {values} = useAutoForm<recommendFieldsType>();
  const isLowesPension = useDeriveIsLowesPension('setUpNew.newPlan');
  const disabled = useMemo(() => {
    if (isLowesPension) {
      // lowes pensions are always valid to contribute or transfer to, as they dont have subplans
      return false;
    }
    return !!setUpNewNewPlanMeta.error;
  }, [setUpNewNewPlanMeta.error, isLowesPension]);
  const {recommendDetails} = useRecommendContext();

  useEffect(() => {
    loadSavedSetUpNewPlan({recommendDetails, setUpNewFieldHelpers}).then(() =>
      setLoaded()
    );
  }, [recommendDetails]);

  if (!values.mainPlanType) {
    return <Typography>Please choose a plan type above</Typography>;
  }

  return (
    <>
      <SourceDestination
        toError={undefined}
        fromError={undefined}
        source={
          <NewPlan
            context="setUpNew"
            formikKeyPrefix="setUpNew.newPlan"
            contactName={contactName}
          />
        }
        sourceLabel={sourceDestinationLabels?.first}
      />
      <SetupNewConvert disabled={disabled} />
    </>
  );
};

export default SetUpNew;
